import React, { useState, useEffect } from 'react';
import UserChatHeader from '../UserChatHeader/UserChatHeader';
import SendMessageBox from '../SendMessageBox/SendMessageBox';
import ChatMessagesList from '../ChatMessagesList/ChatMessagesList';
import ChatMessagesSkeleton from '../ChatMessagesList/ChatMessagesSkeleton';
import PendingChatMessage from '../ChatMessagesList/PendingChatMessage';
import { useSelector } from 'react-redux';
import Button from '../../Button';
import FontAwesomeIcon from '../../Icon';
import { USER_ROLES, PATHS } from 'src/common/constants';
import { useHistory } from 'react-router-dom';
import Loader from '../../Loader';
import {
  useSuiteAPI,
  UseSuiteAPIReturn,
} from 'src/SharedCustomHooks/UseSuiteAPI/useSuiteAPI';
import { buildVideoChatChannelName } from 'src/pages/Inbox/CustomHooks/helpers';
import './inbox-message-chat.scss';

const InboxMessageChat = ({
  selectedItem,
  allowVideoChat,
  chatMessagesList,
  loadingChatMessages,
  pendingChatMessageText,
  onSendChatMessage,
  sendingVideoChatInvite,
  onSendVideoChatInvite,
}) => {
  const user = useSelector((state: any) => state.auth?.user || {});

  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);

  const history: any = useHistory();

  const { fetchSuiteById, isLoadingSuite }: UseSuiteAPIReturn = useSuiteAPI();

  const videoChatChannelName: string = buildVideoChatChannelName(selectedItem);
  const roleName = user.roles[0].role.name;

  const scrollTargetId =
    selectedItem.referenceType === 'Ticket'
      ? 'scroll-to-bottom1'
      : 'scroll-to-bottom';

  const canStartVideoChat: boolean =
    allowVideoChat === true && roleName !== USER_ROLES.REALTOR;

  useEffect(() => {
    clearState();
    scrollToBottom();
  }, [selectedItem, chatMessagesList]);

  const scrollToBottom = () => {
    let scroll_to_bottom = document.getElementById(scrollTargetId);
    scroll_to_bottom.scrollTop = scroll_to_bottom.scrollHeight;
  };

  const onSetMessage = (value) => {
    setMessage(value);
  };

  const onSetAttachments = (file) => {
    setAttachments(file);
  };

  const sendMessage = async () => {
    const messageText = message;
    const attachment = attachments[0] || null;

    clearState();
    requestAnimationFrame(scrollToBottom);

    const { ok } = await onSendChatMessage(messageText, attachment);

    if (!ok) {
      setMessage(messageText);
      setAttachments(attachment ? [attachment] : []);
    }
  };

  const clearState = () => {
    setMessage('');
    setAttachments([]);
  };

  const startVideoChat = async (chatMessage) => {
    const videoChatRoute = {
      pathname: PATHS.VIDEO_CHAT,
      state: {
        chatId: videoChatChannelName,
        sentByName: chatMessage.sentByName,
        sentToName: chatMessage.sentToName,
        url: PATHS.INBOX,
      },
    };

    const { ok } = await onSendVideoChatInvite({
      thread: selectedItem,
      message: chatMessage,
    });

    if (!ok) {
      return;
    }

    history.push(videoChatRoute);
  };

  const icon: any = selectedItem.referenceType === 'Suite' ? 'building' : 'wrench';

  let extraInfo = null;

  if (selectedItem.referenceType === 'Ticket') {
    extraInfo = {
      propertyName: selectedItem?.propertyName || '',
      itemName: selectedItem?.itemName || '',
      issueType: selectedItem?.issueType || '',
    };
  }

  const navigateToSuite = async () => {
    const { ok, suite } = await fetchSuiteById(selectedItem.referenceId);

    if (!ok) {
      return;
    }

    const suiteRoute = {
      pathname: PATHS.SUITES_VIEW.replace(
        ':id',
        selectedItem.propertyId + '&' + selectedItem.referenceId
      ),
      state: {
        suite,
        property: selectedItem.propertyId,
        navigateTo: PATHS.INBOX,
      },
    };

    history.push(suiteRoute);
  };

  const onNavigate = () => {
    if (selectedItem.referenceType === 'Ticket') {
      const serviceRequestRoute = {
        pathname: PATHS.SERVICE_REQUESTS_VIEW.replace(
          ':id',
          selectedItem.referenceId
        ),
        state: { navigateTo: PATHS.INBOX },
      };
      history.push(serviceRequestRoute);
      return;
    }

    navigateToSuite();
  };

  return (
    <div className="inbox-message-chat">
      <UserChatHeader
        title={selectedItem?.ticketNumber || ''}
        referenceType={selectedItem?.referenceType || ''}
        description={selectedItem?.description || ''}
        profileIcon={icon}
        address={selectedItem?.address || ''}
        extraInfo={extraInfo}
        onNavigate={onNavigate}
        from={undefined}
      />
      {(isLoadingSuite || sendingVideoChatInvite) && <Loader center={true} />}
      <div id={scrollTargetId}>
        {loadingChatMessages ? (
          <ChatMessagesSkeleton />
        ) : (
          <>
            <ChatMessagesList
              chatMessagesList={chatMessagesList}
              currentUserId={user.id}
              canStartVideoChat={canStartVideoChat}
              onStartVideoChat={startVideoChat}
            />
            {pendingChatMessageText !== null && (
              <PendingChatMessage messageText={pendingChatMessageText} />
            )}
          </>
        )}
      </div>
      {attachments.length > 0 && (
        <div className="attachment-preview">
          <span>
            {attachments[0].path}
            <Button
              color="semantic"
              label={<FontAwesomeIcon glyph="trash" weight="light" />}
              onClick={() => setAttachments([])}
            />{' '}
          </span>
        </div>
      )}
      <SendMessageBox
        message={message}
        onSetMessage={onSetMessage}
        onSetAttachments={onSetAttachments}
        onSendMessage={sendMessage}
        canSend={Boolean(message) || attachments.length !== 0}
      />
    </div>
  );
};

export default InboxMessageChat;